/**
 * scheduleHelpers.js
 * Shared formatting and calculation utilities for schedule components (LeagueSchedule, UserSchedule).
 */

const PLACEHOLDER_LOCATIONS = new Set([
  "field location",
  "tbd location",
  "location tbd",
  "unknown venue",
  "tbd",
]);

export const getOrdinalSuffix = (day) => {
  const n = Number(day);
  if (!Number.isFinite(n)) return "";
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const formatDateLabel = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const day = d.getDate();
  return `${weekday}, ${month} ${day}${getOrdinalSuffix(day)}`;
};

export const formatTimeLabel = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
    .replace(" ", "");
};

export const formatFieldLabel = (rawField) => {
  if (!rawField) return null;
  const str = String(rawField).trim();
  if (!str) return null;
  return /^\d+$/.test(str) ? `Field ${str}` : str;
};

export const isGameCanceled = (game) => {
  const status = String(game?.status || "")
    .trim()
    .toLowerCase();
  return status === "canceled" || status === "cancelled";
};

export const buildAppleMapsUrl = (locationName, locationAddress) => {
  const rawName = String(locationName || "").trim();
  const cleanName = PLACEHOLDER_LOCATIONS.has(rawName.toLowerCase())
    ? ""
    : rawName;
  const cleanAddress = String(locationAddress || "").trim();

  if (cleanName && cleanAddress) {
    return `https://maps.apple.com/?q=${encodeURIComponent(cleanName)}&address=${encodeURIComponent(cleanAddress)}`;
  }
  if (cleanName) {
    return `https://maps.apple.com/?q=${encodeURIComponent(cleanName)}`;
  }
  if (cleanAddress) {
    return `https://maps.apple.com/?q=${encodeURIComponent(cleanAddress)}`;
  }
  return null;
};

const applyGameResultToRecords = (records, game) => {
  if (isGameCanceled(game)) return;
  const {
    home_score: hs,
    away_score: as,
    home_team_id: hId,
    away_team_id: aId,
  } = game;
  if (hs === null || as === null || hs === undefined || as === undefined) {
    return;
  }
  if (!records[hId]) records[hId] = { w: 0, l: 0 };
  if (!records[aId]) records[aId] = { w: 0, l: 0 };
  if (hs > as) {
    records[hId].w += 1;
    records[aId].l += 1;
  } else if (as > hs) {
    records[aId].w += 1;
    records[hId].l += 1;
  }
};

export const calculateTeamRecords = (games = []) => {
  const records = {};
  for (const game of games) {
    applyGameResultToRecords(records, game);
  }
  return records;
};

export const getGameScoreDetails = (game, teamRecords = {}) => {
  const hasScores =
    (game.home_score !== null && game.home_score !== undefined) ||
    (game.away_score !== null && game.away_score !== undefined);

  const awayRecord = teamRecords[game.away_team_id]
    ? `${teamRecords[game.away_team_id].w}-${teamRecords[game.away_team_id].l}`
    : "0-0";
  const homeRecord = teamRecords[game.home_team_id]
    ? `${teamRecords[game.home_team_id].w}-${teamRecords[game.home_team_id].l}`
    : "0-0";

  let awayScoreClass = "tie";
  let homeScoreClass = "tie";
  if (hasScores) {
    if (game.away_score > game.home_score) {
      awayScoreClass = "win";
      homeScoreClass = "loss";
    } else if (game.home_score > game.away_score) {
      homeScoreClass = "win";
      awayScoreClass = "loss";
    }
  }

  return {
    hasScores,
    awayRecord,
    homeRecord,
    awayRecordFormatted: `(${awayRecord})`,
    homeRecordFormatted: `(${homeRecord})`,
    awayScoreClass,
    homeScoreClass,
  };
};

export const getUserTeamOutcome = (game, userTeamIds = []) => {
  if (!game || isGameCanceled(game)) return null;
  const hs = game.home_score;
  const as = game.away_score;
  if (hs === null || hs === undefined || as === null || as === undefined) {
    return null;
  }

  const teamSet = new Set((userTeamIds || []).map(String));
  const isAway = teamSet.has(String(game.away_team_id));
  const isHome = teamSet.has(String(game.home_team_id));

  if ((isAway && isHome) || (!isAway && !isHome)) {
    return null;
  }

  const myScore = isAway ? Number(as) : Number(hs);
  const oppScore = isAway ? Number(hs) : Number(as);

  if (myScore > oppScore) {
    return { letter: "W", statusClass: "win" };
  }
  if (myScore < oppScore) {
    return { letter: "L", statusClass: "loss" };
  }
  return { letter: "T", statusClass: "tie" };
};
